import axios from 'axios';
import { reportApiError } from '@/sentryApi';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { TokenService } from './token';
import { useAppStore } from '@/stores/app';
import { useAuthStore } from '@/stores/auth';

/** Callbacks/controls for {@link ApiService.stream}. */
interface StreamOptions {
  /** Called for every received chunk with the decoded chunk text and the full text aggregated so far. */
  onChunk?: (chunk: string, aggregated: string) => void;
  /** Called once when the stream ends, with the complete aggregated text. */
  onFinish?: (aggregated: string) => void;
  /** AbortSignal to cancel the underlying fetch. */
  signal?: AbortSignal;
}

const ApiService = {
  get baseURL(): string | undefined {
    return axios.defaults.baseURL;
  },

  /** Interceptor ID returned by axios when the 401 interceptor is mounted; null when unmounted. */
  _401interceptor: null as number | null, // 401 interceptor

  init(baseURL: string): void {
    axios.defaults.baseURL = baseURL;
    //axios.defaults.withCredentials = true;

    // if environment is not production, set x-moinai-dev header
    if (import.meta.env.MODE !== 'production') {
      axios.defaults.headers.common['x-moinai-dev'] = import.meta.env.VITE_HEADER_SECRET;
    }
  },

  setHeader(): void {
    axios.defaults.headers.common['Authorization'] = `Bearer ${TokenService.getToken()}`;
    axios.defaults.headers.common["X-CSRF-Token"] = TokenService.getCSRFToken();
  },

  removeHeader(): void {
    axios.defaults.headers.common = {};
  },

  get(resource: string, config?: AxiosRequestConfig): Promise<AxiosResponse> {
    return axios.get(resource, config);
  },

  post(resource: string, data?: any, timeout: number = 60000): Promise<AxiosResponse> {
    return axios.post(resource, data, {timeout});
  },

  patch(resource: string, data?: any, timeout: number = 60000): Promise<AxiosResponse> {
    return axios.patch(resource, data, {timeout});
  },

  put(resource: string, data?: any): Promise<AxiosResponse> {
    return axios.put(resource, data);
  },

  delete(resource: string, data?: any): Promise<AxiosResponse> {
    return axios.delete(resource, {
      headers: {
        Authorization: `Bearer ${TokenService.getToken()}`,
        ['X-CSRF-Token']: TokenService.getCSRFToken(),
      },
      data: data,
    });
  },
  /** POST multipart/form-data (file uploads etc.). */
  postData(ressource: string, formData: FormData): Promise<AxiosResponse> {
    return axios.post(ressource, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // NOTE: intentionally does not return/await the fetch chain (fire-and-forget); consumers rely on the callbacks
  async stream(resource: string, { onChunk, onFinish, signal }: StreamOptions = {}): Promise<void> {
    fetch((this.baseURL as string) + resource, {
      signal,
      method: 'GET',
      headers: {
        ...axios.defaults.headers.common,
        ...axios.defaults.headers.get,
    } as HeadersInit }).then(res => {
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let aggregated = '';

      return reader.read().then(function process({ done, value }: ReadableStreamReadResult<Uint8Array>): any {
        if (done) {
          if (onFinish) onFinish(aggregated);
          return { data: aggregated };
        }
        const chunk = decoder.decode(value, { stream: true });
        aggregated += chunk;
        if (onChunk) {
          onChunk(chunk, aggregated);
        }
        return reader.read().then(process);
      });
    });
  },

  /**
     * Perform a custom Axios request.
     *
     * data is an object containing the following properties:
     *  - method
     *  - url
     *  - data ... request payload
     *  - auth (optional)
     *    - username
     *    - password
     **/
  customRequest(data: AxiosRequestConfig): Promise<AxiosResponse> {
    return axios(data);
  },

  mount401Interceptor(): void {
    this._401interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // 5xx → Sentry (no-op without a DSN); before any of the handling below reshapes the error.
        reportApiError(error);

        // if version txt file fetching failed, do nothing
        if (error && error.config && error.config.url === `${import.meta.env.VITE_HUB_URL}/version.txt`) {
          // do nothing
          return;
        }

        // Call "await this.get(`/bot`)" will change the Bot if we receive Error from backend request.
        // After Error Middleware has been added to back end. We can handle code errors here:
        // error.response.data should have { code: number, message: string, status: httpStatus }
        if (error.response?.data) {
          const { code, message, status } = error.response.data;

          // Return direct response when we have all 3 parameters
          if (error.response.data?.hasOwnProperty('code') && message && status) {
            throw error.response.data;
            // return Promise.reject({ code, message, status });
          }
        }

        if (!error.response && !axios.isCancel(error)) {
          setTimeout(() => useAppStore().handleConnectionLost(), 1.5 * 1000);
        } else if (error.request?.status == 401) {
          if (error.config?.url === `/bot`) {
            // Dev server only: a restarted API invalidates every issued JWT, which used to
            // dump the developer back on the login form. Re-authenticate with the configured
            // dev account and retry instead. No-op unless `.env.development.local` provides
            // credentials; never present in a production bundle (see src/dev/devAutoLogin.ts).
            if (import.meta.env.DEV) {
              const { recoverDevSession } = await import('@/dev/devAutoLogin');
              const retryConfig = await recoverDevSession(error.config);
              if (retryConfig) return axios(retryConfig);
            }
            // Logout the user
            useAuthStore().logout();
            throw error;
          } else {
            await this.get(`/bot`);
          }
        }

        // If error was not 401 just reject as is
        throw error;
      }
    );
  },

  unmount401Interceptor(): void {
    // Eject the interceptor
    axios.interceptors.response.eject(this._401interceptor as number);
  },
};

export default ApiService;
